import { useEffect, useState } from "react";

import { db } from "../../../util/firebase";
import {
  query,
  collection,
  where,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  addDoc,
} from "firebase/firestore";

import BarLoader from "react-spinners/BarLoader";

import { useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import { FaRegCircleCheck } from "react-icons/fa6";

import memberships from "../../../config/memberships";

function PaymentSuccessfulPage() {
  const { t } = useTranslation("common");

  const params = useParams();
  const orderUid = params.orderUid;

  const [isLoading, setIsLoading] = useState(false);

  const grantItems = async (order) => {
    for (const item of order.items || []) {
      if (item.itemCategory === "MEMBERSHIP") {
        const tier = item.itemUid?.includes("platinum")
          ? "platinum"
          : item.itemUid?.includes("gold")
          ? "gold"
          : "silver";

        const userRef = query(
          collection(db, "users"),
          where("uid", "==", order.userUid)
        );
        const findUsers = await getDocs(userRef);
        for (const userObject of findUsers.docs) {
          const getUser = doc(db, "users", userObject.id);
          const userData = userObject.data() || {};

          const now = new Date();
          const currentExpiry = userData.membershipExpiryDate
            ? new Date(userData.membershipExpiryDate.seconds * 1000)
            : null;

          const hasActive = currentExpiry && currentExpiry > now;
          let newExpiry;
          if (hasActive && userData.membershipTier === tier) {
            newExpiry = new Date(currentExpiry);
            newExpiry.setMonth(newExpiry.getMonth() + 1);
          } else {
            newExpiry = new Date();
            newExpiry.setMonth(newExpiry.getMonth() + 1);
          }

          // per-spec perks sourced from centralized config
          const perks = memberships[tier] || memberships.silver;

          const newTickets = (userData.tickets || 0) + perks.monthlyBonus;
          const newStarCoins = (userData.starCoins || 0) + perks.starCoinsBonus;

          await updateDoc(getUser, {
            membershipTier: tier,
            membershipExpiryDate: newExpiry,
            tickets: newTickets,
            starCoins: newStarCoins,
            monthlyBonusTickets: perks.monthlyBonus,
            lastMonthlyBonusGrantedAt: new Date(),
            ticketMultiplier: perks.ticketMultiplier,
            discountedEntryFees: perks.discountedEntryFees,
            redeemableGiftCardRange: perks.redeemableRange,
            avatarDecoration: perks.avatarDecoration,
          });
        }

        continue;
      }
      // BUNDLE: grant contained products and mark purchasers
      if (item.itemCategory === "BUNDLE") {
        // mark bundle purchaser
        const bundleRef = query(
          collection(db, "bundles"),
          where("uid", "==", item.itemUid)
        );
        const findBundles = await getDocs(bundleRef);
        for (const bundleObject of findBundles.docs) {
          const getBundle = doc(db, "bundles", bundleObject.id);
          await updateDoc(getBundle, {
            purchaserUids: arrayUnion(order.userUid),
          });
        }

        // process each item inside the bundle
        for (const bundleProduct of item.bundleProducts || []) {
          // STAR COINS inside bundle -> credit user
          if (bundleProduct.category === "STAR COINS") {
            const qty = Number(bundleProduct.item || 1);
            const amountFromName =
              Number(
                String(bundleProduct.name || "")
                  .split(" ")[0]
                  .replace(/,/g, "")
              ) || 0;
            const amount = amountFromName * qty;
            const userRef = query(
              collection(db, "users"),
              where("uid", "==", order.userUid)
            );
            const findUsers = await getDocs(userRef);
            for (const userObject of findUsers.docs) {
              const getUser = doc(db, "users", userObject.id);
              const userData = userObject.data() || {};
              await updateDoc(getUser, {
                starCoins: (userData.starCoins || 0) + amount,
              });
            }
          }

          // AVATAR ICONS inside bundle -> add beneficiary
          if (bundleProduct.category === "AVATAR ICONS") {
            const avatarRef = query(
              collection(db, "avatars"),
              where("productUid", "==", bundleProduct.uid)
            );
            const findAvatars = await getDocs(avatarRef);
            for (const avatarObject of findAvatars.docs) {
              const getAvatar = doc(db, "avatars", avatarObject.id);
              await updateDoc(getAvatar, {
                beneficiaryUids: arrayUnion(order.userUid),
              });
            }
          }

          // PROFILE BANNERS inside bundle -> add beneficiary
          if (bundleProduct.category === "PROFILE BANNERS") {
            const bannerRef = query(
              collection(db, "banners"),
              where("productUid", "==", bundleProduct.uid)
            );
            const findBanners = await getDocs(bannerRef);
            for (const bannerObject of findBanners.docs) {
              const getBanner = doc(db, "banners", bannerObject.id);
              await updateDoc(getBanner, {
                beneficiaryUids: arrayUnion(order.userUid),
              });
            }
          }

          // mark purchaser on the product record (if there is a products collection)
          if (bundleProduct.uid) {
            const productRef = query(
              collection(db, "products"),
              where("uid", "==", bundleProduct.uid)
            );
            const findProducts = await getDocs(productRef);
            for (const productObject of findProducts.docs) {
              const getProduct = doc(db, "products", productObject.id);
              await updateDoc(getProduct, {
                purchaserUids: arrayUnion(order.userUid),
              });
            }
          }
        }

        continue;
      }

      // STAR COINS (single product)
      if (item.itemCategory === "STAR COINS") {
        const amountFromName =
          Number(
            String(item.itemName || "")
              .split(" ")[0]
              .replace(/,/g, "")
          ) || 0;
        const qty = Number(item.itemQuantity || 1);
        const amount = amountFromName * qty;
        const userRef = query(
          collection(db, "users"),
          where("uid", "==", order.userUid)
        );
        const findUsers = await getDocs(userRef);
        for (const userObject of findUsers.docs) {
          const getUser = doc(db, "users", userObject.id);
          const userData = userObject.data() || {};
          await updateDoc(getUser, {
            starCoins: (userData.starCoins || 0) + amount,
          });
        }

        // mark purchaser on product record
        if (item.itemUid) {
          const productRef = query(
            collection(db, "products"),
            where("uid", "==", item.itemUid)
          );
          const findProducts = await getDocs(productRef);
          for (const productObject of findProducts.docs) {
            const getProduct = doc(db, "products", productObject.id);
            await updateDoc(getProduct, {
              purchaserUids: arrayUnion(order.userUid),
            });
          }
        }

        continue;
      }

      // AVATAR ICONS (single product)
      if (item.itemCategory === "AVATAR ICONS") {
        const avatarRef = query(
          collection(db, "avatars"),
          where("productUid", "==", item.itemUid)
        );
        const findAvatars = await getDocs(avatarRef);
        for (const avatarObject of findAvatars.docs) {
          const getAvatar = doc(db, "avatars", avatarObject.id);
          await updateDoc(getAvatar, {
            beneficiaryUids: arrayUnion(order.userUid),
          });
        }

        // mark purchaser on product record
        if (item.itemUid) {
          const productRef = query(
            collection(db, "products"),
            where("uid", "==", item.itemUid)
          );
          const findProducts = await getDocs(productRef);
          for (const productObject of findProducts.docs) {
            const getProduct = doc(db, "products", productObject.id);
            await updateDoc(getProduct, {
              purchaserUids: arrayUnion(order.userUid),
            });
          }
        }

        continue;
      }

      // PROFILE BANNERS (single product)
      if (item.itemCategory === "PROFILE BANNERS") {
        const bannerRef = query(
          collection(db, "banners"),
          where("productUid", "==", item.itemUid)
        );
        const findBanners = await getDocs(bannerRef);
        for (const bannerObject of findBanners.docs) {
          const getBanner = doc(db, "banners", bannerObject.id);
          await updateDoc(getBanner, {
            beneficiaryUids: arrayUnion(order.userUid),
          });
        }

        // mark purchaser on product record
        if (item.itemUid) {
          const productRef = query(
            collection(db, "products"),
            where("uid", "==", item.itemUid)
          );
          const findProducts = await getDocs(productRef);
          for (const productObject of findProducts.docs) {
            const getProduct = doc(db, "products", productObject.id);
            await updateDoc(getProduct, {
              purchaserUids: arrayUnion(order.userUid),
            });
          }
        }

        continue;
      }

      // ENTRY (tournament entry) -> add user to room members
      if (item.itemCategory === "ENTRY") {
        try {
          const roomUid = item.metadata?.roomUid;
          if (!roomUid) continue;
          // If this is a VIP Exclusive entry, ensure the purchaser has an active Platinum membership
          const entryType = item.metadata?.type;
          if (entryType === "vipExclusive") {
            const userRefChk = query(
              collection(db, "users"),
              where("uid", "==", order.userUid)
            );
            const foundUsersChk = await getDocs(userRefChk);
            const purchaser = foundUsersChk.docs[0]?.data() || {};
            const expiry = purchaser.membershipExpiryDate;
            const hasActive =
              expiry && new Date(expiry.seconds * 1000) > new Date();
            if (!(hasActive && purchaser.membershipTier === "platinum")) {
              try {
                await addDoc(collection(db, "audit_invalid_entry_grants"), {
                  orderUid: order.uid || orderUid,
                  item,
                  userUid: order.userUid,
                  reason: "User not Platinum for VIP Exclusive entry",
                  timestamp: new Date(),
                });
              } catch (auditErr) {
                console.error(
                  "Failed to write audit log for skipped VIP grant:",
                  auditErr
                );
              }
              // skip granting this entry
              continue;
            }
          }
          const roomRef = query(
            collection(db, "rooms"),
            where("uid", "==", roomUid)
          );
          const findRooms = await getDocs(roomRef);
          for (const roomObject of findRooms.docs) {
            const getRoom = doc(db, "rooms", roomObject.id);
            const roomData = roomObject.data() || {};
            const newMemberUids = Array.isArray(roomData.memberUids)
              ? [...roomData.memberUids]
              : [];
            if (!newMemberUids.includes(order.userUid))
              newMemberUids.push(order.userUid);

            const newMembers = Array.isArray(roomData.members)
              ? [...roomData.members]
              : [];
            newMembers.push({
              name: order.userUsername || order.userUid,
              email: order.userEmail || null,
              points: 0,
              starPoints: 0,
              starCoins: 0,
              challengesWon: 0,
              total: 0,
              uid: order.userUid,
            });

            await updateDoc(getRoom, {
              memberUids: newMemberUids,
              members: newMembers,
            });
          }
        } catch (e) {
          console.error("Failed to add user to room from ENTRY item:", e);
        }

        continue;
      }
    }
  };

  const completeOrder = async () => {
    try {
      setIsLoading(true);

      const orderRef = query(
        collection(db, "orders"),
        where("uid", "==", orderUid)
      );
      const findOrders = await getDocs(orderRef);
      for (const orderObject of findOrders.docs) {
        const getOrder = doc(db, "orders", orderObject.id);
        const orderData = orderObject.data();
        if (orderData.status === "Pending") {
          try {
            await grantItems(orderData);
            await updateDoc(getOrder, { status: "Complete" });
          } catch (grantErr) {
            console.error("Granting items failed:", grantErr);
            await updateDoc(getOrder, { status: "Failed" });
          }
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      alert("An error occured while completing the order");
    }
  };

  useEffect(() => {
    if (orderUid) {
      completeOrder();
    }
    // eslint-disable-next-line
  }, [orderUid]);

  const navigate = useNavigate();

  if (isLoading)
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{
            width: 100,
            marginBottom: "1rem",
          }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );

  return (
    <div id="paymentSuccessfulPage" className="page">
      <div id="paymentSuccessfulInnerContainer">
        <FaRegCircleCheck />
        <h1>{t("paymentSuccessful").toUpperCase()}!</h1>
        <p style={{ color: "#FFFFFF" }}>{t("thankYouForYourPurchase")}</p>
        <Button onClick={() => navigate("/shop")} className="primaryBtn">
          {t("backToShop").toUpperCase()}
        </Button>
      </div>
    </div>
  );
}

export default PaymentSuccessfulPage;
